import React, { useRef, useEffect } from 'react';
import { TweenLite, Circ } from 'gsap';

function getDistance(p1, p2) {
  return Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2);
}

class Circle {
  constructor(pos, rad, color, ctx) {
    this.pos = pos;
    this.radius = rad;
    this.color = color;
    this.ctx = ctx;
    this.active = 0;
  }

  draw() {
    if (!this.active) return;
    this.ctx.beginPath();
    this.ctx.arc(this.pos.x, this.pos.y, this.radius, 0, 2 * Math.PI);
    this.ctx.fillStyle = `rgba(156,217,249,${this.active})`;
    this.ctx.fill();
  }
}

const GlobalAnimatedBackground = () => {
  const canvasRef = useRef(null);
  const points = useRef([]);
  const target = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });

  useEffect(() => {
    const canvas = canvasRef.current;
    
    // Check if canvas exists before proceeding
    if (!canvas) {
      console.warn('Canvas element not found');
      return;
    }

    let width = window.innerWidth;
    let height = window.innerHeight;
    const ctx = canvas.getContext('2d');
    
    // Additional check for context
    if (!ctx) {
      console.warn('Could not get 2D context from canvas');
      return;
    }
    
    canvas.width = width;
    canvas.height = height;

    // Create points
    points.current = [];
    for (let x = 0; x < width; x += width / 20) {
      for (let y = 0; y < height; y += height / 20) {
        const px = x + (Math.random() * width) / 20;
        const py = y + (Math.random() * height) / 20;
        const p = { x: px, originX: px, y: py, originY: py };
        points.current.push(p);
      }
    }

    // Find closest points
    points.current.forEach(p1 => {
      p1.closest = points.current
        .filter(p2 => p1 !== p2)
        .sort((a, b) => getDistance(p1, a) - getDistance(p1, b))
        .slice(0, 5);
    });

    // Add circles
    points.current.forEach(p => {
      p.circle = new Circle(p, 2 + Math.random() * 2, 'rgba(255,255,255,0.3)', ctx);
    });

    function mouseMove(e) {
      target.current.x = e.clientX;
      target.current.y = e.clientY;
    }

    function resize() {
      width = window.innerWidth;
      height = document.body.scrollHeight;
      canvas.width = width;
      canvas.height = height;
    }

    function drawLines(p, ctx) {
      if (!p.active) return;
      p.closest.forEach(closestP => {
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(closestP.x, closestP.y);
        ctx.strokeStyle = `rgba(156,217,249,${p.active})`;
        ctx.stroke();
      });
    }

    function shiftPoint(p) {
      TweenLite.to(p, 1 + 1 * Math.random(), {
        x: p.originX - 50 + Math.random() * 100,
        y: p.originY - 50 + Math.random() * 100,
        ease: Circ.easeInOut,
        onComplete: () => shiftPoint(p),
      });
    }

    points.current.forEach(p => shiftPoint(p));

    function animate() {
      ctx.clearRect(0, 0, width, height);
      points.current.forEach(p => {
        const dist = getDistance(target.current, p);
        p.active = dist < 4000 ? 0.3 : dist < 20000 ? 0.1 : dist < 40000 ? 0.02 : 0;
        p.circle.active = p.active * 2;
        drawLines(p, ctx);
        p.circle.draw();
      });
      requestAnimationFrame(animate);
    }

    window.addEventListener('mousemove', mouseMove);
    window.addEventListener('resize', resize);

    animate();

    return () => {
      window.removeEventListener('mousemove', mouseMove);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: -50,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
      }}
    />
  );
};

export default GlobalAnimatedBackground; 